import { CampaignEditor, EditorConfig, Modification } from '@core/types';
import { ConfigManager } from '@core/config';
import { createLogger } from '@core/logger';
import { SelectorEngine } from '@runtime/SelectorEngine';
import { Panel, Highlighter } from './ui';
import { ElementPicker } from './picker';
import { SessionManager, EditorAPI } from './api';
import { SDK_VERSION, APP_NAME } from '@shared/constants';
import { CampaignError } from '@shared/errors';
import { normalizePath } from '@shared/utils/dom';

export class Editor implements CampaignEditor {
  private config!: ConfigManager;
  private logger = createLogger();
  private selectorEngine = new SelectorEngine();
  private panel: Panel;
  private highlighter: Highlighter;
  private elementPicker: ElementPicker;
  private sessionManager!: SessionManager;
  private isInitialized = false;
  private modifications: Modification[] = [];

  constructor() {
    this.panel = new Panel();
    this.highlighter = new Highlighter(this.selectorEngine);
    this.elementPicker = new ElementPicker(this.selectorEngine);
    this.setupEventHandlers();
  }

  init(config: EditorConfig): void {
    if (this.isInitialized) {
      this.logger.warn('Editor already initialized');
      return;
    }

    try {
      this.config = new ConfigManager(config, true);
      this.logger = createLogger(this.config.get('debug'));
      this.logger.info('Initializing Campaign Editor', { version: SDK_VERSION });

      // Initialize API
      const api = new EditorAPI(
        this.config.get('platformOrigin') as string,
        this.config.getEndpoint('verify'),
      );

      this.sessionManager = new SessionManager(
        api,
        (config as EditorConfig).sessionToken,
        window.location.href,
      );

      // Setup panel
      this.setupPanel();

      // Start initialization flow
      this.initialize();

      this.isInitialized = true;
      this.logger.info('Campaign Editor initialized successfully');
    } catch (error) {
      this.logger.error('Failed to initialize editor', error);
      this.panel.setStatus('Initialization failed. Check console for details.');
      throw error;
    }
  }

  private async initialize(): Promise<void> {
    try {
      this.panel.setStatus('Verifying session...');
      this.panel.setEnabled(false);

      const sessionData = await this.sessionManager.verify();

      this.panel.setStatus('Session verified. Loading modifications...');

      // Set session timer if expires_at is available
      if (sessionData?.expires_at) {
        this.panel.setSessionTimer(sessionData.expires_at);
      }

      this.modifications = await this.sessionManager.loadModifications();

      this.panel.setStatus('Editor ready. Click an element to select it.');
      this.panel.setEnabled(true);
      this.panel.renderModifications(this.modifications);

      this.startElementPicking();
      this.logger.info('Editor initialization complete');
    } catch (error) {
      this.logger.error('Editor initialization failed', error);
      this.panel.setStatus(
        error instanceof CampaignError ? error.message : 'Initialization failed. Please try again.',
      );
      throw error;
    }
  }

  private setupPanel(): void {
    const currentPath = normalizePath(window.location.pathname);

    this.panel.setPath(currentPath);
    this.panel.setVersion(APP_NAME);
  }

  private setupEventHandlers(): void {
    // Panel events
    this.panel.on('close', () => this.handleExit());
    this.panel.on('save', () => this.saveModifications());
    this.panel.on('clear', () => this.clearModifications());
    this.panel.on('modificationAdd', (mod: Modification) => this.addModification(mod));
    this.panel.on('modificationRemove', (index: number) => this.removeModification(index));
    this.panel.on('modificationReorder', (fromIndex: number, toIndex: number) =>
      this.reorderModifications(fromIndex, toIndex),
    );
  }

  private startElementPicking(): void {
    this.elementPicker.start((element: Element, selector: string) => {
      this.panel.setSelector(selector);
      this.highlighter.show(selector);

      const detectedAction = this.elementPicker.detectAction(element);
      const actionSelect = this.getActionSelect();
      actionSelect.value = detectedAction;

      this.panel.updateActionFields();

      const smartValue = this.elementPicker.getSmartValue(element, detectedAction);
      if (smartValue) {
        this.panel.setValue(smartValue);
      }
    });
  }

  private getActionSelect(): HTMLSelectElement {
    return this.panel.getActionSelect();
  }

  private addModification(modification: Modification): void {
    this.modifications.push(modification);
    this.panel.renderModifications(this.modifications);
    this.highlighter.show(modification.selector);
    this.logger.info('Added modification', modification);
  }

  private removeModification(index: number): void {
    if (index >= 0 && index < this.modifications.length) {
      this.modifications.splice(index, 1);
      this.panel.renderModifications(this.modifications);
      this.logger.info('Removed modification at index', index);
    }
  }

  private reorderModifications(fromIndex: number, toIndex: number): void {
    if (
      fromIndex >= 0 &&
      fromIndex < this.modifications.length &&
      toIndex >= 0 &&
      toIndex < this.modifications.length
    ) {
      const temp = this.modifications[toIndex];
      this.modifications[toIndex] = this.modifications[fromIndex];
      this.modifications[fromIndex] = temp;

      this.panel.renderModifications(this.modifications);
      this.logger.info('Reordered modifications', { fromIndex, toIndex });
    }
  }

  private clearModifications(): void {
    this.modifications = [];
    this.panel.renderModifications(this.modifications);
    this.highlighter.clear();
    this.logger.info('Cleared all modifications');
  }

  private getReturnUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('return');
  }

  private redirectOrDestroy(): void {
    const returnUrl = this.getReturnUrl();
    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      this.destroy();
    }
  }

  private handleExit(): void {
    // Disable panel and show loading state
    this.panel.setEnabledWithExitButton(false, true);
    this.panel.setStatus('Exiting editor...');

    // Small delay to show the loading state, then redirect
    setTimeout(() => this.redirectOrDestroy(), 500);
  }

  private async saveModifications(): Promise<void> {
    try {
      this.panel.setStatus('Saving modifications...');
      this.panel.setEnabledWithExitButton(false, true);

      await this.sessionManager.saveModifications(this.modifications);

      this.panel.setStatus('Saved successfully! Redirecting...');
      this.logger.info('Modifications saved successfully');

      // Redirect after successful save without re-enabling the panel
      setTimeout(() => this.redirectOrDestroy(), 1500);
    } catch (error) {
      this.logger.error('Failed to save modifications', error);
      this.panel.setStatus(
        error instanceof CampaignError
          ? error.message
          : 'Failed to save modifications. Please try again.',
      );
      // Only re-enable on error
      this.panel.setEnabled(true);
    }
    // Note: No finally block - we don't re-enable after successful save
  }

  show(): void {
    this.panel.show();
  }

  hide(): void {
    this.panel.hide();
  }

  destroy(): void {
    this.elementPicker.destroy();
    this.highlighter.destroy();
    this.panel.destroy();
    this.isInitialized = false;
    this.logger.info('Campaign Editor destroyed');
  }
}
